import DOMPurify from 'dompurify';

/**
 * Everything the admin-side editors can emit: Quill's contact-description toolbar (headings,
 * bold/italic/underline/strike, ordered + bullet lists, links) plus the plain block markup the
 * API's system messages carry. Nothing here can load a remote resource or run script — no
 * `img`, `svg`, `iframe`, `base`, `style` — so the allow-list is the whole defence.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'div', 'span',
  'strong', 'b', 'em', 'i', 'u', 's',
  'a', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'blockquote',
];

/** `data-list` marks Quill 2's bullet items (it renders every list as an `<ol>`); `class` carries its `ql-*` hooks. */
const ALLOWED_ATTR = ['href', 'class', 'data-list'];

/** Only schemes a resident can act on. Everything else — `javascript:`, `data:`, relative — is dropped. */
const ALLOWED_URI_REGEXP = /^(?:https?|mailto|tel):/i;

/**
 * A bare `<a href>` in the native WebView would navigate the app shell itself, so every surviving
 * link is forced out to the system browser with its handle on the opener cut. Author-supplied
 * `target`/`rel` never reach here — they are not allowed attributes, so ours always win.
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/** Admin-authored HTML, reduced to the tags and links that are safe to hand to `dangerouslySetInnerHTML`. */
export function sanitizeHtml(html: string): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP });
}

/**
 * A broadcast body is a rendered email rather than a description field, so it legitimately carries
 * pictures, rules and table layout that `sanitizeHtml` has no reason to permit. Images are the only
 * remote load allowed, and only over https or as an inline `data:` image, so nothing here can reach
 * a script sink. `MESSAGE_BODY_CLASS` in `features/communication/messageHtml.ts` styles this set.
 */
const MESSAGE_TAGS = [
  ...ALLOWED_TAGS,
  'img', 'hr', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
];

const MESSAGE_ATTR = [...ALLOWED_ATTR, 'src', 'alt', 'title', 'width', 'height', 'colspan', 'rowspan'];

/** The rendered body of a communication, safe to hand to `dangerouslySetInnerHTML`. */
export function sanitizeMessageHtml(html: string): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: MESSAGE_TAGS,
    ALLOWED_ATTR: MESSAGE_ATTR,
    ALLOWED_URI_REGEXP,
    ADD_DATA_URI_TAGS: ['img'],
  });
}
